import { selectUser } from "~/state/user/selectors";
import { store } from "~/state/store";
import { watch, watchPremiumData, watchSubscriptions, watchUser } from "~/state/util";
import { node, createAvatar, createPremiumIcon } from "~/util/dom";
import { CONFIG } from "~/config";
import { Unsubscribe } from "redux";
import { ELoginState, IUserAccount, IUserInfo } from "~/state/user/interfaces";
import { _ } from "~/i18n";
import { selectSubscriptions } from "~/state/share-position/selectors";
import { ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import SznNotifications from "~/seno";
import { getItem, setItem } from "~/es6/localstorage";
import { filterSubscriptions } from "~/share-position2/utils";
import createProfileButton from "~/ui/profile/profilebutton";
import { SETTINGS_24, SHARED_LOCATION_24 } from "~/util/sds-compatible-icons";
import createSDSIcon from "~/ui/icon";
import { createPremiumPromo } from "~/ui/profile/premiumPromo";
import { openPropositionDialog } from "~/util/premium";

export default class Profile {
	_container: HTMLDivElement;

	_button: HTMLButtonElement;

	_avatar: HTMLSpanElement;

	_bigAvatar: HTMLSpanElement;

	_notifications: SznNotifications;

	_debugToolsShow: boolean;

	_storeUnsubscribeUser: Unsubscribe;

	_storeUnsubscribePremium: Unsubscribe;

	_sharePositionNew: HTMLSpanElement;

	_sharePositionCount: HTMLSpanElement;

	_sharePositionSpan: HTMLSpanElement;

	_sharePositionButton: ReturnType<typeof createProfileButton>;

	_storeUnsubscribeSharePosition: Unsubscribe;

	_premiumPromo: ReturnType<typeof createPremiumPromo>;

	_premiumStatusButton: ReturnType<typeof createProfileButton>;

	_loggedItems: HTMLDivElement;

	constructor() {
		this._container = node("div", { className: `profile-multi` });
		this._button = node("button", { type: "button", className: "icon profile" });
		this._avatar = node("span", { className: "avatar-small" });
		this._bigAvatar = node("span", { className: `avatar-big-multi` });
		this._notifications = null;
		this._debugToolsShow = false;
		this._button.setAttribute("data-log-text", "stateswitch-medailonek");
		this._bigAvatar.addEventListener("click", this, false);

		const wtUser = watch(store.getState, "user", watchUser);

		this._storeUnsubscribeUser = store.subscribe(wtUser(newUser => {
			const { user, accounts } = newUser;
			const state = store.getState();
			const subscriptions = filterSubscriptions(selectSubscriptions(state));

			this._buildAvatar(user);
			this._buildButton(user);
			this._build(user, accounts, subscriptions);

			if (this._notifications && Mapy.getComponent("notificationList").isActive()) {
				Mapy.getComponent("notificationList").deactivate();
			}
		}));

		const wtUserPremium = watch(store.getState, "user", watchPremiumData);

		this._storeUnsubscribePremium = store.subscribe(wtUserPremium(newUser => {
			this._buildAvatar(newUser.user);

			if (newUser.user.premium) {
				this._premiumPromo?.remove();

				if (Mapy.CUS.user.premiumProfile) {
					this._premiumStatusButton && this._loggedItems?.prepend(this._premiumStatusButton);
				}
			} else {
				this._premiumStatusButton?.remove();

				if (Mapy.CUS.user.premiumPay) {
					this._premiumPromo && this._loggedItems?.prepend(this._premiumPromo);
				}
			}
		}));

		if (Mapy.CUS.userweb?.generalSharePosition) {
			const wtSharePositionSubscriptions = watch(store.getState, "sharePosition.subscriptions", watchSubscriptions);

			this._storeUnsubscribeSharePosition = store.subscribe(wtSharePositionSubscriptions(subscriptions => {
				const filteredSubscriptions = filterSubscriptions(subscriptions);

				this._sharePositionButton.setAttribute("data-log-count", filteredSubscriptions.length.toString());
				this._sharePositionCount.innerHTML = filteredSubscriptions.length.toString();

				if (!filteredSubscriptions.length && Mapy.CUS.userweb?.generalSharePosition && !getItem("sharePosition2New")) {
					this._sharePositionCount.remove();
					this._sharePositionSpan.appendChild(this._sharePositionNew);
				} else {
					this._sharePositionNew.remove();
					this._sharePositionSpan.appendChild(this._sharePositionCount);
				}
			}));
		} else {
			const wtSharePositionSubscriptions = watch(store.getState, "sharePosition.subscriptions", watchSubscriptions);

			this._storeUnsubscribeSharePosition = store.subscribe(wtSharePositionSubscriptions(subscriptions => {
				this._sharePositionButton.setAttribute("data-log-count", subscriptions.length.toString());
				if (subscriptions.length) {
					this._sharePositionCount.innerHTML = subscriptions.length.toString();
					this._sharePositionSpan.appendChild(this._sharePositionCount);
				} else {
					this._sharePositionCount.remove();
				}
			}));
		}

		// notifikace
		this._notifications = new SznNotifications({
			url: `wss://notifikace.mapy.${CONFIG.TLD}`,
		});
		this._notifications.interactiveButtons = false;
		// klikani na ikony
		this._notifications.stacks.forEach(stack => {
			stack.button.addEventListener("click", () => {
				const notify = Mapy.getComponent("notificationList");

				notify.setState({
					stack,
				});
				notify.activate();
			});
		});

		const state = store.getState();
		const { user, accounts } = selectUser(state);
		const subscriptions = filterSubscriptions(selectSubscriptions(state));

		this._sharePositionNew = node("span", { className: "profile__sharing-new" }, _("sharePosition.profileTagNew"));
		this._sharePositionCount = node("span", { className: "profile__sharing-count" });
		this._sharePositionSpan = node("span", { className: "profile__sharing-span" }, _("sharePosition.profileLink"));
		this._sharePositionButton = createProfileButton();
		this._sharePositionButton.append(createSDSIcon({
			symbol: SHARED_LOCATION_24,
		}), this._sharePositionSpan);
		this._sharePositionButton.setAttribute("data-log-ignore", "true");
		this._sharePositionButton.setAttribute("data-log-count", subscriptions.length.toString());
		this._sharePositionButton.addEventListener("click", () => {
			Mapy.getComponent("logger").log("click", {
				text: "Sdílení polohy",
				subscriptionCount: parseInt(this._sharePositionButton.getAttribute("data-log-count")),
			});
			Mapy.getComponent("sharePosition").activate();
			Mapy.getComponent("history").save();
			setItem("sharePosition2New", "1");
			this._sharePositionNew.remove();
			this._sharePositionCount.innerHTML = subscriptions.length.toString();
			this._sharePositionSpan.appendChild(this._sharePositionCount);
		});
		this._buildAvatar(user);
		this._buildButton(user);
		this._build(user, accounts, subscriptions);
	}

	destructor() {
		this._storeUnsubscribeUser?.();
		this._storeUnsubscribePremium?.();
		this._storeUnsubscribeSharePosition?.();
		if (this._notifications) {
			this._notifications.node.removeEventListener("unread-change", this);
		}
	}

	get avatar() {
		return this._avatar;
	}

	get bigAvatar() {
		return this._bigAvatar;
	}

	get button() {
		return this._button;
	}

	get container() {
		return this._container;
	}

	/**
	 * check na moznost zobrazeni debug tlacitka
	 * @param {Boolean} zobrazit
	 */
	checkDebug(show) {
		this._debugToolsBtn(show);
	}

	rebuild() {
		const state = store.getState();
		const { user, accounts } = selectUser(state);
		const subscriptions = selectSubscriptions(state);

		this._build(user, accounts, subscriptions);
	}

	// eslint-disable-next-line class-methods-use-this
	handleEvent(event) {
		if (event.type === "click") {
			const header = event.target.closest("div.header");
			const link = header.querySelector("a");

			if (link) {
				link.click();
			}
		}
	}

	_buildButton(user: IUserInfo) {
		this._button.innerHTML = "";
		Mapy.getComponent("layout").getBar().classList.remove("items-4");

		if (user) {
			if (user.state === ELoginState.Green) {
				Mapy.getComponent("layout").getBar().classList.add("items-4");
				this._button.appendChild(this._avatar);
			}
		} else {
			Mapy.getComponent("stateswitch").setState(Mapy.getComponent("mymaps"));
		}
	}

	_buildAvatar(user: IUserInfo) {
		this._avatar.innerHTML = "";
		this._bigAvatar.innerHTML = "";
		this._bigAvatar.className = `avatar-big-multi`;

		if (user && user.avatar !== "") {
			this._avatar.appendChild(createAvatar(user.avatar, 32, user.premium ? "mapy" : user.sbr ? "sbr" : "none"));
			this._bigAvatar.appendChild(createAvatar(user.avatar, 48, user.premium ? "mapy" : "none"));
			this._bigAvatar.classList.add(user.state);
		}
	}

	_build(user: IUserInfo, accounts: Array<IUserAccount>, subscriptions: Array<ISubscriptionWithDescription>) {
		this._container.innerHTML = "";
		this._premiumPromo = null;
		this._premiumStatusButton = null;
		this._loggedItems = null;

		if (user && (user.state === ELoginState.Green || user.state === ELoginState.Orange)) {
			const PROFILE_URL = `https://ucet.seznam.${CONFIG.TLD}${CONFIG.loginLang && CONFIG.loginLang !== "cs" ? `/?lang=${CONFIG.loginLang}` : ""}`;
			const LOGGED_USERS_URL = `https://login.szn.${CONFIG.TLD}/logged-users`;
			const header = node("div", { className: "header" });
			const name = node("p");
			const link = node("a", { target: "_blank", rel: "noopener", href: user.state === ELoginState.Green ? PROFILE_URL : "#" });

			link.dataset.logText = "usernameActive";
			link.dataset.logUrl = PROFILE_URL;
			link.dataset.logSection = "accounts";

			if (user.state === ELoginState.Orange) {
				link.addEventListener("click", event => {
					event.preventDefault();

					Mapy.getComponent("login").open({
						username: user.accountDisplayName,
						type: user.accountDisplayName ? null : "relogin",
						motivation: "none",
					}).then(
						() => {
							Mapy.getComponent("mymaps").activate();
							Mapy.getComponent("history").save();
						},
						() => {},
					);
				});
			}

			link.appendChild(node("strong", {}, user.name));
			link.appendChild(node("span", {}, user.accountDisplayName));
			name.appendChild(link);

			header.appendChild(this.bigAvatar);
			header.appendChild(name);
			if (this._notifications && user.state === ELoginState.Green) {
				header.appendChild(this._notifications.node);
			}

			// odkaz na profil na Seznamu
			const profile = createProfileButton({
				href: user.state === ELoginState.Green ? PROFILE_URL : "#",
				text: user.state === ELoginState.Green ? _("profile.profile2") : _("login.login"),
				target: "_blank",
			});

			profile.dataset.logSection = "accounts";

			if (user.state === ELoginState.Orange) {
				profile.addEventListener("click", event => {
					event.preventDefault();

					Mapy.getComponent("login").open({
						username: user.accountDisplayName,
						type: user.accountDisplayName ? null : "relogin",
						motivation: "none",
					}).then(
						() => {
							Mapy.getComponent("mymaps").activate();
							Mapy.getComponent("history").save();
						},
						() => {},
					);
				});
			}

			// odhlasovaci tlacitko
			const logout = createProfileButton({
				text: user.state === ELoginState.Green ? _("login.logout") : _("login.login.forget"),
			});

			logout.dataset.logSection = "accounts";

			if (user.state === ELoginState.Green) {
				logout.dataset.logText = "logout";
			}

			logout.addEventListener("click", () => {
				Mapy.getComponent("login").logout(user.state === ELoginState.Orange).finally(() => {
					setTimeout(() => {
						// eslint-disable-next-line @typescript-eslint/no-shadow
						const { user, accounts } = selectUser(store.getState());

						if ((user && user.state === ELoginState.Orange) || (accounts && accounts.length)) {
							Mapy.getComponent("profile").deactivate();
							Mapy.getComponent("profile").rebuild();
							Mapy.getComponent("profile").activate();
						} else {
							Mapy.getComponent("mymaps").activate(false, true);
						}

						Mapy.getComponent("history").save();
					}, 200);
				});
			});

			const login = createProfileButton({
				text: _("profile.anotherAccount"),
			});

			login.dataset.logSection = "accounts";
			login.addEventListener("click", () => {
				Mapy.getComponent("login").open({
					type: "relogin",
					analyticsData: {
						loginOrigin: "accountDialog",
						section: "accounts",
					},
					motivation: "none",
				}).then(
					() => {
						Mapy.getComponent("mymaps").activate();
						Mapy.getComponent("history").save();
					},
					() => {},
				);
			});

			const manage = createProfileButton({
				target: "_blank",
				href: LOGGED_USERS_URL,
				text: _("profile.manageAccounts"),
			});

			manage.dataset.logSection = "accounts";

			const list = node("ul");

			if (accounts) {
				for (let index = 0; index < accounts.length; index++) {
					const account = accounts[index];

					if (account.userId === user.rusId) {
						continue;
					}

					const item = node("li");
					const namePara = node("p");
					const avatarCont = node("span", { className: `avatar-big-multi ${account.state}` });

					namePara.appendChild(node("strong", {}, account.name));
					namePara.appendChild(node("span", {}, account.accountDisplayName));

					avatarCont.appendChild(createAvatar(account.avatar_url, 48, account.flags.premium ? "mapy" : "none"));

					item.appendChild(avatarCont);
					item.appendChild(namePara);

					const switchAccount = () => {
						Mapy.getComponent("login").switchUser(account.userId);

						if (!Mapy.getComponent("profile").isActive()) {
							Mapy.getComponent("profile").activate();
							Mapy.getComponent("history").save();
						}

						Mapy.getComponent("logger").log("click", {
							section: "accounts",
							text: "usernameInactive",
						});
					};

					avatarCont.addEventListener("click", switchAccount);
					namePara.addEventListener("click", switchAccount);

					list.appendChild(item);
				}
			}

			this._container.appendChild(header);

			if (user.state === ELoginState.Green || user.state === ELoginState.Orange) {
				const promo = createPremiumPromo();

				// premium
				if (user.premiumData?.availableProducts.length) {
					if (Mapy.CUS.user.premiumPay) {
						promo.setAttribute("data-log-action", "menuPremium_premiumPurchase_show");
						promo.addEventListener("click", () => {
							openPropositionDialog("menuPremium");
						});
					}
				}

				this._premiumPromo = promo;
			}

			if (user.state === ELoginState.Green) {
				const loggedItems = node("div", { className: "profile__section" });
				const premium = createProfileButton({
					text: _("profile.premium.title"),
				});

				this._premiumStatusButton = premium;
				this._loggedItems = loggedItems;

				premium.prepend(createPremiumIcon());
				premium.addEventListener("click", () => {
					Mapy.getComponent("premiumStatus").activate();
				});

				// premium
				if (user.premiumData?.availableProducts.length) {
					if (Mapy.CUS.user.premiumPay) {
						loggedItems.appendChild(this._premiumPromo);
					}
				} else if (Mapy.CUS.user.premiumProfile) {
					loggedItems.appendChild(premium);
				}

				// zapinani sdileni polohy
				if (!subscriptions.length && Mapy.CUS.userweb?.generalSharePosition && !getItem("sharePosition2New")) {
					this._sharePositionCount.remove();
					this._sharePositionSpan.appendChild(this._sharePositionNew);
				} else {
					this._sharePositionNew.remove();
					this._sharePositionCount.innerHTML = subscriptions.length.toString();
					this._sharePositionSpan.appendChild(this._sharePositionCount);
				}

				loggedItems.appendChild(this._sharePositionButton);

				// nastaveni
				const settings = createProfileButton({
					symbol: SETTINGS_24,
					text: _("profile.settings.title"),
				});

				settings.addEventListener("click", () => {
					Mapy.getComponent("settings").activate();
				});
				loggedItems.appendChild(settings);

				this._container.appendChild(loggedItems);
			}

			// sekce s odhlasenim a profilem
			const orangeUserItems = node("div", { className: "profile__section" });

			if (user.state === ELoginState.Orange) {
				if (Mapy.CUS.user.premiumPay) {
					orangeUserItems.appendChild(this._premiumPromo);
				}
				this._loggedItems = orangeUserItems;
			}

			orangeUserItems.append(profile, logout);
			this._container.appendChild(orangeUserItems);

			// dalsi prihlasene ucty
			if (list.childNodes.length) {
				this._container.appendChild(list);
			}

			// sekce akci dalsich uctu
			const otherUsersActions = node("div", { className: "profile__section" });

			otherUsersActions.append(login, manage);
			this._container.appendChild(otherUsersActions);

			// debugovaci tlacitko
			this._debugToolsBtn(this._debugToolsShow);
		}
	}

	_debugToolsBtn(show) {
		if (show) {
			const config = node("div", { className: "profile__section diag" });
			const debugButton = createProfileButton({
				text: "Diagnostika",
			});

			debugButton.addEventListener("click", () => {
				Mapy.openConfigDebug(true);
			});

			config.appendChild(debugButton);
			this._container.append(config);
		} else {
			this._container.querySelector(".diag")?.remove();
		}

		this._debugToolsShow = show;
	}
}
